import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, CheckCircle, Users } from 'lucide-react';
import { useCoursePromo } from '../contexts/CoursePromoContext';
import { trackAcademyBannerClick, trackPromoPopupView, trackPromoPopupClose } from '../lib/analytics';

const CoursePromoPopup = () => {
  // Deshabilitado temporalmente - retornar null para no mostrar el popup
  return null;
  
  const { shouldShow, handleClose, handleCTAClick } = useCoursePromo();

  // Track popup view when it's shown
  useEffect(() => {
    if (shouldShow) {
      trackPromoPopupView();
    }
  }, [shouldShow]);

  const handleVisitWebsite = () => {
    // Track the click BEFORE opening the window
    trackAcademyBannerClick();

    // Open the academy website
    window.open('https://www.automationacademy.online', '_blank');

    // Update internal state
    handleCTAClick();
  };

  const onClose = () => {
    trackPromoPopupClose();
    handleClose();
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-6 sm:p-8"
          onClick={onClose}
          data-testid="course-promo-popup"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-md w-full max-h-[90vh] overflow-y-auto bg-gradient-to-b from-[#1A1D3D] to-[#0F1229] rounded-2xl border border-white/10 shadow-2xl"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-text-muted hover:text-text-primary transition-colors bg-black/30 rounded-full p-1.5 hover:bg-black/50"
              data-testid="close-popup-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header Section with Logo */}
            <div className="relative pt-12 pb-4 px-4 sm:pb-6 sm:px-6 bg-gradient-to-b from-transparent to-[#1A1D3D]/50">
              {/* Background Glow Effect */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />

              <div className="relative flex justify-center mb-3 sm:mb-4">
                <div className="w-32 h-32 sm:w-36 sm:h-36 flex items-center justify-center ">
                  <img
                    src="/logo.png"
                    alt="n8n play logo"
                    className="w-32 h-32 sm:w-36 sm:h-36 object-contain"
                  />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-lg sm:text-2xl font-heading font-bold text-center mb-1 sm:mb-2 leading-tight">
                <span className="text-text-primary">Crea tu agencia de</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                  automatización con n8n
                </span>
              </h2>
              <p className="text-base sm:text-lg font-heading font-bold text-center text-white mb-1 sm:mb-2">
                en solo 6 semanas
              </p>
            </div>

            {/* Content Section */}
            <div className="px-4 pb-6 sm:px-8 sm:pb-8">
              {/* Description */}
              <p className="text-sm sm:text-base text-text-secondary text-center mb-4 sm:mb-6 leading-relaxed">
                Domina n8n paso a paso, sin ser programador, y aprende a diseñar, vender y entregar sistemas de automatización para clientes reales.
              </p>

              {/* Benefits */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-start gap-2 sm:gap-3 bg-white/5 backdrop-blur-sm p-2.5 sm:p-3 rounded-lg border border-primary/20">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-text-primary text-xs sm:text-sm">Domina n8n y automatiza procesos complejos</span>
                </div>
                <div className="flex items-start gap-2 sm:gap-3 bg-white/5 backdrop-blur-sm p-2.5 sm:p-3 rounded-lg border border-primary/20">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-text-primary text-xs sm:text-sm">Diseña paquetes de alto valor (High Ticket)</span>
                </div>
                <div className="flex items-start gap-2 sm:gap-3 bg-white/5 backdrop-blur-sm p-2.5 sm:p-3 rounded-lg border border-primary/20">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-text-primary text-xs sm:text-sm">Clases 100% en vivo + Comunidad exclusiva</span>
                </div>
              </div>

              {/* Price Section */}
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Oferta de lanzamiento</p>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <span className="text-3xl sm:text-4xl font-heading font-bold text-white">$100</span>
                      <span className="text-lg sm:text-xl text-text-muted line-through">$200</span>
                      <span className="text-xs sm:text-sm text-white">USD</span>
                    </div>
                  </div>
                  <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full">
                    50% OFF
                  </div>
                </div>
                <p className="text-xs text-text-secondary">Solo 25 plazas disponibles por cohorte</p>
              </div>

              {/* Social Proof */}
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 p-2.5 sm:p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 border-2 border-background flex items-center justify-center">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-700 border-2 border-background" />
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-600 border-2 border-background" />
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 border-2 border-background flex items-center justify-center text-white text-xs font-bold">
                    +15
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-text-primary text-sm font-medium">20+ estudiantes ya se inscribieron</p>
                  <p className="text-primary text-xs font-bold">¡Solo quedan 5 lugares!</p>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleVisitWebsite}
                className="w-full bg-gradient-to-r from-[#FF6B6B] to-[#EA4B71] hover:from-[#FF5555] hover:to-[#E03A61] text-white font-heading font-bold py-3 sm:py-4 text-sm sm:text-base rounded-lg flex items-center justify-center gap-2 sm:gap-3 shadow-xl hover:shadow-2xl transition-all transform hover:scale-[1.02]"
                data-testid="visit-website-btn"
              >
                <span>Aplicar al programa</span>
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Disclaimer */}
              <p className="text-xs text-text-muted text-center mt-3 sm:mt-4">
                Visita automationacademy.online para más información
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CoursePromoPopup;